import React, { useState, useEffect } from 'react';
import { ExositeEntry, ExtractionEvidence, IndividualExositeEntry } from '../types';

interface ExositeCardProps {
  entry: ExositeEntry;
  evidence?: ExtractionEvidence[];
  onViewStructure?: (pdbId: string) => void;
  onViewEvidence?: (entryId: number) => void;
  onViewProteinDetails?: (proteinName: string) => void;
}

// Simple SVG icons
const BeakerIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2z" />
  </svg>
);

const DocumentTextIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const CheckCircleIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ExclamationTriangleIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);

const ChevronDownIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m19.5 8.25-7.5 7.5-7.5-7.5" />
  </svg>
);

const ChevronUpIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m4.5 15.75 7.5-7.5 7.5 7.5" />
  </svg>
);

const LinkIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
  </svg>
);

const EyeIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);

const FlaskIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428 13 9v-5a2 2 0 0 0-2-2 2 2 0 0 0-2 2v5l-6.428 6.428A2 2 0 0 0 4 18v1a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-1a2 2 0 0 0-1.428-2.572Z" />
  </svg>
);

const TrophyIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M15.75 4.236c-.982.143-1.954.317-2.916.52a6.003 6.003 0 0 0-1.668-3.084 3.759 3.759 0 0 1 1.668 3.084 8.155 8.155 0 0 1 2.916-.52m-7.5 0c.982.143 1.954.317 2.916.52a6.003 6.003 0 0 1 1.668-3.084 3.759 3.759 0 0 0-1.668 3.084 8.155 8.155 0 0 0-2.916-.52" />
  </svg>
);

const MedalIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M15.75 4.236c-.982.143-1.954.317-2.916.52a6.003 6.003 0 0 0-1.668-3.084 3.759 3.759 0 0 1 1.668 3.084 8.155 8.155 0 0 1 2.916-.52m-7.5 0c.982.143 1.954.317 2.916.52a6.003 6.003 0 0 1 1.668-3.084 3.759 3.759 0 0 0-1.668 3.084 8.155 8.155 0 0 0-2.916-.52" />
  </svg>
);

// Helper function to generate robust manuscript links with fallbacks
const generateManuscriptLink = (entry: IndividualExositeEntry): { url: string; label: string } | null => {
  // Priority order: manuscript_url > DOI > PMC > PubMed
  
  // 1. Try manuscript_url first (if valid)
  if (entry.manuscript_url && entry.manuscript_url.trim()) {
    const url = entry.manuscript_url.trim();
    // Basic URL validation
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('www.')) {
      const fullUrl = url.startsWith('www.') ? `https://${url}` : url;
      return { url: fullUrl, label: 'View Paper' };
    }
  }
  
  // 2. Try DOI
  if (entry.doi && entry.doi.trim()) {
    const doi = entry.doi.trim();
    const url = doi.startsWith('http') ? doi : `https://doi.org/${doi}`;
    return { url: url, label: 'View Paper (DOI)' };
  }
  
  // 3. Try PMC
  if (entry.pmc_id && entry.pmc_id.trim()) {
    const pmcId = entry.pmc_id.trim().replace(/^PMC/, ''); // Remove PMC prefix if present
    return { url: `https://www.ncbi.nlm.nih.gov/pmc/articles/PMC${pmcId}/`, label: 'View Paper (PMC)' };
  }
  
  // 4. Try PubMed
  if (entry.pubmed_id && entry.pubmed_id.trim()) {
    const pubmedId = entry.pubmed_id.trim();
    return { url: `https://pubmed.ncbi.nlm.nih.gov/${pubmedId}/`, label: 'View Paper (PubMed)' };
  }
  
  // No valid link found
  return null;
};

const ExositeCard: React.FC<ExositeCardProps> = ({ 
  entry, 
  evidence = [], 
  onViewStructure,
  onViewEvidence,
  onViewProteinDetails
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'evidence' | 'structures' | 'individual' | 'conflicts'>('details');
  
  // Mobile responsiveness
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isSmallMobile = windowWidth < 480;
  
  // Helper function to handle PDB IDs (array or comma-separated string)
  const getPdbIdArray = (pdbIds: any): string[] => {
    if (!pdbIds) return [];
    if (Array.isArray(pdbIds)) {
      return pdbIds;
    }
    if (typeof pdbIds === 'string') {
      const split = pdbIds.split(',').map(id => id.trim());
      return split;
    }
    return [];
  };
  const [selectedIndividualEntry, setSelectedIndividualEntry] = useState<IndividualExositeEntry | null>(() => {
    if (entry.entry_type === 'grouped' && entry.grouped_entry?.best_entry) {
      return entry.grouped_entry.best_entry;
    }
    if (entry.entry_type === 'individual' && entry.individual_entry) {
      return entry.individual_entry;
    }
    return null;
  });

  // Helper to get current data based on entry type
  const isGrouped = entry.entry_type === 'grouped';
  const groupedData = entry.grouped_entry;
  const individualData = entry.individual_entry;

  const getConfidenceColor = (level?: string) => {
    switch (level) {
      case 'HIGH':
        return { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' };
      case 'MEDIUM':
        return { backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fde68a' };
      case 'LOW':
        return { backgroundColor: '#fecaca', color: '#991b1b', borderColor: '#fca5a5' };
      default:
        return { backgroundColor: '#f1f5f9', color: '#374151', borderColor: '#e2e8f0' };
    }
  };

  const getValidationIcon = () => {
    if (isGrouped) {
      const validatedCount = groupedData?.validated_count || 0;
      const totalCount = groupedData?.entry_count || 0;
      if (validatedCount > totalCount / 2) {
        return <CheckCircleIcon style={{ height: isMobile ? '16px' : '20px', width: isMobile ? '16px' : '20px', color: '#059669' }} />;
      }
    } else if (individualData?.validation_passed) {
      return <CheckCircleIcon style={{ height: isMobile ? '16px' : '20px', width: isMobile ? '16px' : '20px', color: '#059669' }} />;
    }
    return <ExclamationTriangleIcon style={{ height: isMobile ? '16px' : '20px', width: isMobile ? '16px' : '20px', color: '#d97706' }} />;
  };

  const formatResidues = (residues?: string) => {
    if (!residues) return 'Not specified';
    
    // Parse and format residue notation - truncate for mobile
    const formattedResidues = residues.split(',').map(residue => residue.trim()).join(', ');
    if (isMobile && formattedResidues.length > 50) {
      return formattedResidues.substring(0, 50) + '...';
    }
    return formattedResidues;
  };

  const getDisplayName = () => {
    if (isGrouped) {
      return groupedData?.protein_name || 'Unknown Protein';
    }
    return individualData?.protein_name || 'Unknown Protein';
  };

  const getCurrentConfidenceLevel = () => {
    if (isGrouped) {
      const highCount = groupedData?.high_confidence_count || 0;
      const totalCount = groupedData?.entry_count || 0;
      if (highCount > totalCount / 2) return 'HIGH';
      if (highCount > 0) return 'MEDIUM';
      return 'LOW';
    }
    return individualData?.confidence_level || 'LOW';
  };

  const confidenceStyle = getConfidenceColor(getCurrentConfidenceLevel());

  return (
    <div className="card" style={{ 
      transition: 'box-shadow 0.2s',
      cursor: 'default'
    }}>
      {/* Header - Mobile Responsive */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'space-between', 
        marginBottom: isMobile ? '12px' : '16px',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '12px' : '0'
      }}>
        <div style={{ flex: 1, width: isMobile ? '100%' : 'auto' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '6px' : '12px', 
            marginBottom: '8px',
            flexWrap: 'wrap'
          }}>
            <h3 style={{ 
              fontSize: isMobile ? '18px' : '20px', 
              fontWeight: '600', 
              color: '#1e293b', 
              margin: 0,
              cursor: isGrouped && onViewProteinDetails ? 'pointer' : 'default',
              lineHeight: '1.2',
              wordBreak: 'break-word'
            }}
            onClick={isGrouped && onViewProteinDetails ? () => onViewProteinDetails(getDisplayName()) : undefined}
            onMouseEnter={(e) => {
              if (isGrouped && onViewProteinDetails) {
                e.currentTarget.style.color = '#3b82f6';
              }
            }}
            onMouseLeave={(e) => {
              if (isGrouped && onViewProteinDetails) {
                e.currentTarget.style.color = '#1e293b';
              }
            }}>
              {getDisplayName()}
            </h3>
            
            {/* Badge container with responsive wrapping */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: isMobile ? '4px' : '6px',
              alignItems: 'center'
            }}>
              {/* Entry Type / Family Badge */}
              {isGrouped ? (
                <span style={{
                  padding: isMobile ? '1px 6px' : '2px 8px',
                  fontSize: isMobile ? '10px' : '12px',
                  fontWeight: '600',
                  borderRadius: '9999px',
                  backgroundColor: '#f0f9ff',
                  color: '#1d4ed8',
                  border: '1px solid #bfdbfe'
                }}>
                  {groupedData?.entry_count} {isMobile ? 'ST' : 'STUDIES'}
                </span>
              ) : (
                selectedIndividualEntry?.family_class && (
                  <span style={{
                    padding: isMobile ? '1px 6px' : '2px 8px',
                    fontSize: isMobile ? '9px' : '11px',
                    fontWeight: '500',
                    borderRadius: '9999px',
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                    border: '1px solid #fde68a',
                    maxWidth: isMobile ? '120px' : '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={selectedIndividualEntry.family_class}
                  >
                    {selectedIndividualEntry.family_class}
                  </span>
                )
              )}
              
              {/* Confidence Badge */}
              <span style={{
                padding: isMobile ? '1px 6px' : '2px 8px',
                fontSize: isMobile ? '10px' : '12px',
                fontWeight: '500',
                borderRadius: '9999px',
                border: `1px solid ${confidenceStyle.borderColor}`,
                ...confidenceStyle
              }}>
                {getCurrentConfidenceLevel()}
              </span>
              
              {/* Conflict Warning for Grouped Entries */}
              {isGrouped && groupedData?.validation_summary?.has_conflicts && (
                <span style={{
                  padding: isMobile ? '1px 4px' : '2px 8px',
                  fontSize: isMobile ? '9px' : '11px',
                  fontWeight: '600',
                  borderRadius: '4px',
                  backgroundColor: '#fef2f2',
                  color: '#dc2626',
                  border: '1px solid #fecaca'
                }}>
                  {isMobile ? '⚠' : '⚠ CONFLICTS'}
                </span>
              )}
            </div>
          </div>
          
          {/* Info row - Mobile responsive */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '8px' : '16px', 
            fontSize: isMobile ? '12px' : '14px', 
            color: '#64748b', 
            flexWrap: 'wrap' 
          }}>
            {/* PDB Structure Info */}
            {((isGrouped ? (groupedData?.unique_pdb_count || 0) : (individualData?.pdb_id ? 1 : 0)) > 0) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <BeakerIcon style={{ height: '14px', width: '14px' }} />
                <span>
                  {isGrouped ? (
                    `${groupedData?.unique_pdb_count} PDB${isMobile ? '' : ' Structures'}`
                  ) : (
                    `PDB: ${individualData?.pdb_id}`
                  )}
                </span>
              </div>
            )}
            
            {/* UniProt Info - Hide on small mobile */}
            {(!isGrouped && individualData?.uniprot_id && !isSmallMobile) && (
              <span>UniProt: {individualData.uniprot_id}</span>
            )}
            
            {/* Organism Info - Truncate on mobile */}
            {(!isGrouped && individualData?.organism) && (
              <span style={{ fontStyle: 'italic' }}>
                {isMobile && individualData.organism.length > 15 
                  ? individualData.organism.substring(0, 15) + '...'
                  : individualData.organism
                }
              </span>
            )}
            
            {/* Publication Year Range for Grouped */}
            {isGrouped && groupedData?.publication_year_range && (
              <span>{isMobile ? 'Yrs: ' : 'Years: '}{groupedData.publication_year_range}</span>
            )}
            
            {/* Publication Year for Individual */}
            {!isGrouped && individualData?.publication_year && (
              <span>{isMobile ? 'Yr: ' : 'Year: '}{individualData.publication_year}</span>
            )}
          </div>
        </div>

        {/* Action buttons - Mobile responsive */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? '4px' : '8px',
          flexShrink: 0,
          width: isMobile ? '100%' : 'auto',
          justifyContent: isMobile ? 'space-between' : 'flex-end'
        }}>
          {/* Structure Viewing Buttons */}
          {onViewStructure && (
            <div style={{ display: 'flex', gap: isMobile ? '2px' : '4px' }}>
              {isGrouped ? (
                (() => {
                  const pdbIds = getPdbIdArray(groupedData?.representative_pdb_ids);
                  return pdbIds.length > 0 && (
                    <button
                      onClick={() => {
                        onViewStructure(pdbIds[0]);
                      }}
                      className="btn-secondary"
                      style={{ 
                        fontSize: isMobile ? '12px' : '14px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px',
                        padding: isMobile ? '6px 8px' : '8px 12px'
                      }}
                    >
                      <EyeIcon style={{ height: isMobile ? '14px' : '16px', width: isMobile ? '14px' : '16px' }} />
                      {!isSmallMobile && <span>3D View</span>}
                    </button>
                  );
                })()
              ) : (
                individualData?.pdb_id && (() => {
                  const pdbIds = getPdbIdArray(individualData.pdb_id);
                  const firstPdbId = pdbIds[0] || individualData.pdb_id;
                  return (
                    <button
                      onClick={() => {
                        onViewStructure(firstPdbId);
                      }}
                      className="btn-secondary"
                      style={{ 
                        fontSize: isMobile ? '12px' : '14px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px',
                        padding: isMobile ? '6px 8px' : '8px 12px'
                      }}
                    >
                      <EyeIcon style={{ height: isMobile ? '14px' : '16px', width: isMobile ? '14px' : '16px' }} />
                      {!isSmallMobile && <span>3D View</span>}
                    </button>
                  );
                })()
              )}
            </div>
          )}
          
          {/* View Protein Details for Grouped Entries */}
          {isGrouped && onViewProteinDetails && (
            <button
              onClick={() => onViewProteinDetails(getDisplayName())}
              className="btn-secondary"
              style={{ 
                fontSize: isMobile ? '12px' : '14px', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px',
                padding: isMobile ? '6px 8px' : '8px 12px'
              }}
            >
              <FlaskIcon style={{ height: isMobile ? '14px' : '16px', width: isMobile ? '14px' : '16px' }} />
              {!isSmallMobile && <span>View All ({groupedData?.entry_count})</span>}
            </button>
          )}
          
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="btn-secondary"
            style={{ padding: isMobile ? '6px' : '8px' }}
          >
            {isExpanded ? (
              <ChevronUpIcon style={{ height: isMobile ? '14px' : '16px', width: isMobile ? '14px' : '16px' }} />
            ) : (
              <ChevronDownIcon style={{ height: isMobile ? '14px' : '16px', width: isMobile ? '14px' : '16px' }} />
            )}
          </button>
        </div>
      </div>

      {/* Quick Summary - Mobile Responsive */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: isMobile ? '12px' : '16px', 
        marginBottom: isMobile ? '12px' : '16px' 
      }}>
        <div>
          <p style={{
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '4px'
          }}>
            Exosite Residues
            {isGrouped && groupedData?.validation_summary?.has_conflicts && (
              <span style={{
                fontSize: isMobile ? '10px' : '11px',
                color: '#dc2626',
                fontWeight: '600',
                marginLeft: '8px'
              }}>
                ⚠ {isMobile ? 'CONFLICT' : 'CONFLICTED'}
              </span>
            )}
          </p>

          {/* Reported Residues (As published) */}
          {!isGrouped && selectedIndividualEntry?.exosite_residues_reported && (
            <>
              <p style={{
                fontSize: isMobile ? '10px' : '11px',
                color: '#6b7280',
                fontWeight: '500',
                margin: '0 0 2px 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                As Reported
              </p>
              <p style={{
                color: '#1e293b',
                fontFamily: 'monospace',
                fontSize: isMobile ? '11px' : '13px',
                backgroundColor: '#f8fafc',
                padding: isMobile ? '4px 6px' : '4px 8px',
                borderRadius: '4px',
                margin: '0 0 8px 0',
                wordBreak: 'break-all',
                lineHeight: '1.3',
                borderLeft: '3px solid #d1d5db'
              }}>
                {formatResidues(selectedIndividualEntry.exosite_residues_reported)}
              </p>
            </>
          )}

          {/* UniProt Residues */}
          <p style={{
            fontSize: isMobile ? '10px' : '11px',
            color: '#6b7280',
            fontWeight: '500',
            margin: '0 0 2px 0',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {!isGrouped && selectedIndividualEntry?.exosite_residues_reported ? 'UniProt' : ''}
          </p>
          <p style={{
            color: '#1e293b',
            fontFamily: 'monospace',
            fontSize: isMobile ? '12px' : '14px',
            backgroundColor: isGrouped && groupedData?.validation_summary?.has_conflicts ? '#fef2f2' : '#eff6ff',
            padding: isMobile ? '6px 8px' : '6px 10px',
            borderRadius: '4px',
            margin: 0,
            wordBreak: 'break-all',
            lineHeight: '1.4',
            borderLeft: '3px solid #3b82f6',
            fontWeight: '500'
          }}>
            {formatResidues(
              isGrouped ?
                (groupedData?.consensus_residues || (isMobile ? 'Multiple conflicts' : 'Multiple conflicting reports - click to expand')) :
                (selectedIndividualEntry?.exosite_residues_uniprot || selectedIndividualEntry?.exosite_residues_standard || 'Not specified')
            )}
          </p>
        </div>
        
        <div>
          <p style={{ 
            fontSize: isMobile ? '13px' : '14px', 
            fontWeight: '500', 
            color: '#374151', 
            marginBottom: '4px' 
          }}>
            Binding Partner{isGrouped ? 's' : ''}
          </p>
          <p style={{ 
            color: '#1e293b', 
            margin: 0,
            fontSize: isMobile ? '12px' : '14px',
            lineHeight: '1.3'
          }}>
            {isGrouped ? (
              (() => {
                const partners = groupedData?.common_binding_partners?.slice(0, isMobile ? 2 : 3).join(', ') || 'Multiple partners';
                const remaining = (groupedData?.common_binding_partners?.length || 0) - (isMobile ? 2 : 3);
                return (
                  <>
                    {partners}
                    {remaining > 0 && (
                      <span style={{ color: '#64748b', fontSize: isMobile ? '11px' : '13px' }}>
                        {' '}+{remaining} more
                      </span>
                    )}
                  </>
                );
              })()
            ) : (
              selectedIndividualEntry?.binding_partner || 'Not specified'
            )}
          </p>
        </div>
      </div>

      {/* Publication Info - Mobile Responsive */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        padding: isMobile ? '8px' : '12px', 
        backgroundColor: '#f8fafc', 
        borderRadius: '8px', 
        marginBottom: isMobile ? '12px' : '16px',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '0'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          width: isMobile ? '100%' : 'auto'
        }}>
          <DocumentTextIcon style={{ height: '16px', width: '16px', color: '#6b7280' }} />
          <span style={{ 
            fontSize: isMobile ? '12px' : '14px', 
            color: '#374151',
            wordBreak: 'break-word'
          }}>
            {isGrouped ? (
              `${groupedData?.entry_count || 0} studies ${groupedData?.publication_year_range ? `(${groupedData.publication_year_range})` : ''}`
            ) : (
              `${selectedIndividualEntry?.journal_name || 'Unknown Journal'} ${selectedIndividualEntry?.publication_year ? `(${selectedIndividualEntry.publication_year})` : ''}`
            )}
          </span>
        </div>
        
{!isGrouped && selectedIndividualEntry && (() => {
          const paperLink = generateManuscriptLink(selectedIndividualEntry);
          return paperLink && (
            <a
              href={paperLink.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px', 
                color: '#3b82f6', 
                textDecoration: 'none', 
                fontSize: isMobile ? '12px' : '14px', 
                fontWeight: '500',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#2563eb'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#3b82f6'}
            >
              <LinkIcon style={{ height: isMobile ? '14px' : '16px', width: isMobile ? '14px' : '16px' }} />
              <span>{isMobile ? 'Paper' : paperLink.label}</span>
            </a>
          );
        })()}
        
        {isGrouped && !isMobile && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            fontSize: '12px', 
            color: '#64748b' 
          }}>
            <span>✅ {groupedData?.validated_count || 0} validated</span>
            <span>🔥 {groupedData?.high_confidence_count || 0} high confidence</span>
          </div>
        )}
      </div>

      {/* Expanded Content - Mobile Responsive */}
      {isExpanded && (
        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: isMobile ? '12px' : '16px' }}>
          {/* Tab Navigation - Mobile Responsive */}
          <div style={{ 
            display: 'flex', 
            gap: isMobile ? '2px' : '4px', 
            marginBottom: isMobile ? '12px' : '16px',
            flexWrap: 'wrap',
            overflowX: isMobile ? 'auto' : 'visible',
            paddingBottom: isMobile ? '4px' : '0'
          }}>
            <button
              onClick={() => setActiveTab('details')}
              style={{
                padding: isMobile ? '6px 8px' : '8px 12px',
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: '500',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                transition: 'colors 0.2s',
                backgroundColor: activeTab === 'details' ? '#dbeafe' : 'transparent',
                color: activeTab === 'details' ? '#1d4ed8' : '#64748b',
                whiteSpace: 'nowrap'
              }}
            >
              Details
            </button>
            {isGrouped && groupedData?.individual_entries && groupedData.individual_entries.length > 1 && (
              <button
                onClick={() => setActiveTab('individual')}
                style={{
                  padding: isMobile ? '6px 8px' : '8px 12px',
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: '500',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'colors 0.2s',
                  backgroundColor: activeTab === 'individual' ? '#dbeafe' : 'transparent',
                  color: activeTab === 'individual' ? '#1d4ed8' : '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  whiteSpace: 'nowrap'
                }}
              >
                <FlaskIcon style={{ height: isMobile ? '12px' : '16px', width: isMobile ? '12px' : '16px' }} />
                {isMobile ? `Studies (${groupedData.individual_entries.length})` : `Individual Studies (${groupedData.individual_entries.length})`}
              </button>
            )}
            
            {evidence.length > 0 && (
              <button
                onClick={() => setActiveTab('evidence')}
                style={{
                  padding: isMobile ? '6px 8px' : '8px 12px',
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: '500',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'colors 0.2s',
                  backgroundColor: activeTab === 'evidence' ? '#dbeafe' : 'transparent',
                  color: activeTab === 'evidence' ? '#1d4ed8' : '#64748b',
                  whiteSpace: 'nowrap'
                }}
              >
                Evidence ({evidence.length})
              </button>
            )}
          </div>

          {/* Tab Content - Mobile Responsive (keeping existing content but adjusting spacing) */}
          {activeTab === 'details' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '16px' }}>
              {selectedIndividualEntry?.exosite_residues_standard && (
                <div>
                  <p style={{ 
                    fontSize: isMobile ? '13px' : '14px', 
                    fontWeight: '500', 
                    color: '#374151', 
                    marginBottom: '8px' 
                  }}>
                    Exosite Information
                    {selectedIndividualEntry && isGrouped && groupedData?.individual_entries && groupedData.individual_entries.length > 1 && (
                      <span style={{ 
                        fontSize: isMobile ? '10px' : '11px', 
                        color: '#3b82f6', 
                        fontWeight: '400', 
                        marginLeft: '8px'
                      }}>
                        (selected entry)
                      </span>
                    )}
                  </p>
                  <p style={{ 
                    color: '#1e293b', 
                    fontSize: isMobile ? '12px' : '14px', 
                    lineHeight: '1.5', 
                    backgroundColor: '#eff6ff', 
                    padding: isMobile ? '8px' : '12px', 
                    borderRadius: '8px',
                    margin: 0,
                    fontFamily: 'monospace',
                    wordBreak: 'break-all'
                  }}>
                    {selectedIndividualEntry.exosite_residues_standard}
                  </p>
                </div>
              )}

              {selectedIndividualEntry?.orthosteric_site_residues && (
                <div>
                  <p style={{ 
                    fontSize: isMobile ? '13px' : '14px', 
                    fontWeight: '500', 
                    color: '#374151', 
                    marginBottom: '8px' 
                  }}>
                    Orthosteric Site Residues
                    {selectedIndividualEntry && isGrouped && groupedData?.individual_entries && groupedData.individual_entries.length > 1 && (
                      <span style={{ 
                        fontSize: isMobile ? '10px' : '11px', 
                        color: '#3b82f6', 
                        fontWeight: '400', 
                        marginLeft: '8px'
                      }}>
                        (selected entry)
                      </span>
                    )}
                  </p>
                  <p style={{ 
                    color: '#1e293b', 
                    fontSize: isMobile ? '12px' : '14px', 
                    lineHeight: '1.5', 
                    backgroundColor: '#fef3c7', 
                    padding: isMobile ? '8px' : '12px', 
                    borderRadius: '8px',
                    margin: 0,
                    fontFamily: 'monospace',
                    wordBreak: 'break-all'
                  }}>
                    {selectedIndividualEntry.orthosteric_site_residues}
                  </p>
                </div>
              )}

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: isMobile ? '12px' : '16px' 
              }}>
                {selectedIndividualEntry?.experimental_method && (
                  <div>
                    <p style={{ 
                      fontSize: isMobile ? '13px' : '14px', 
                      fontWeight: '500', 
                      color: '#374151', 
                      marginBottom: '4px' 
                    }}>
                      Experimental Method
                      {selectedIndividualEntry && isGrouped && groupedData?.individual_entries && groupedData.individual_entries.length > 1 && (
                        <span style={{ 
                          fontSize: isMobile ? '10px' : '11px', 
                          color: '#3b82f6', 
                          fontWeight: '400', 
                          marginLeft: '8px'
                        }}>
                          (selected entry)
                        </span>
                      )}
                    </p>
                    <p style={{ 
                      color: '#1e293b', 
                      fontSize: isMobile ? '12px' : '14px', 
                      backgroundColor: '#f8fafc', 
                      padding: isMobile ? '6px 8px' : '8px 12px', 
                      borderRadius: '6px',
                      margin: 0,
                      lineHeight: '1.4'
                    }}>
                      {selectedIndividualEntry.experimental_method}
                    </p>
                  </div>
                )}
                
                {selectedIndividualEntry?.disease_relevance && (
                  <div>
                    <p style={{ 
                      fontSize: isMobile ? '13px' : '14px', 
                      fontWeight: '500', 
                      color: '#374151', 
                      marginBottom: '4px' 
                    }}>
                      Disease Relevance
                      {selectedIndividualEntry && isGrouped && groupedData?.individual_entries && groupedData.individual_entries.length > 1 && (
                        <span style={{ 
                          fontSize: isMobile ? '10px' : '11px', 
                          color: '#3b82f6', 
                          fontWeight: '400', 
                          marginLeft: '8px'
                        }}>
                          (selected entry)
                        </span>
                      )}
                    </p>
                    <p style={{ 
                      color: '#1e293b', 
                      fontSize: isMobile ? '12px' : '14px', 
                      backgroundColor: '#fef2f2', 
                      padding: isMobile ? '6px 8px' : '8px 12px', 
                      borderRadius: '6px',
                      margin: 0,
                      lineHeight: '1.4'
                    }}>
                      {selectedIndividualEntry.disease_relevance}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Individual Studies Tab - Mobile responsive adjustments */}
          {activeTab === 'individual' && isGrouped && groupedData?.individual_entries && groupedData.individual_entries.length > 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '16px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                justifyContent: 'space-between',
                marginBottom: '8px',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '8px' : '0'
              }}>
                <h4 style={{ 
                  fontSize: isMobile ? '14px' : '16px', 
                  fontWeight: '600', 
                  color: '#1e293b', 
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <FlaskIcon style={{ height: isMobile ? '16px' : '20px', width: isMobile ? '16px' : '20px', color: '#059669' }} />
                  {isMobile ? `Studies (${groupedData.individual_entries.length})` : `Individual Experimental Entries (${groupedData.individual_entries.length})`}
                </h4>
                {!isMobile && (
                  <div style={{ 
                    display: 'flex',
                    gap: '12px',
                    fontSize: '12px', 
                    color: '#64748b'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <TrophyIcon style={{ height: '12px', width: '12px', color: '#f59e0b' }} />
                      Best Entry
                    </div>
                  </div>
                )}
              </div>
              
              {/* Individual entries with mobile responsive styling */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr', 
                gap: isMobile ? '8px' : '12px' 
              }}>
                {groupedData.individual_entries.map((individualEntry, index) => {
                  const isOptimal = individualEntry === groupedData.best_entry;
                  const isSecondBest = false;
                  const isSelected = individualEntry === selectedIndividualEntry;
                  return (
                    <div 
                      key={individualEntry.id}
                      style={{ 
                        border: `2px solid ${isOptimal ? '#f59e0b' : isSecondBest ? '#6b7280' : isSelected ? '#3b82f6' : '#e2e8f0'}`,
                        borderRadius: '8px', 
                        padding: isMobile ? '12px' : '16px',
                        backgroundColor: isOptimal ? '#fffbeb' : isSecondBest ? '#f8fafc' : isSelected ? '#eff6ff' : 'white',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => setSelectedIndividualEntry(individualEntry)}
                      onMouseEnter={(e) => {
                        if (!isOptimal && !isSecondBest && !isSelected) {
                          e.currentTarget.style.borderColor = '#cbd5e1';
                          e.currentTarget.style.backgroundColor = '#f8fafc';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isOptimal && !isSecondBest && !isSelected) {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      {/* Badge positioning optimized for mobile */}
                      {isOptimal && (
                        <div style={{
                          position: 'absolute',
                          top: '-1px',
                          right: '12px',
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          fontSize: isMobile ? '9px' : '10px',
                          fontWeight: '600',
                          padding: isMobile ? '1px 4px' : '2px 6px',
                          borderRadius: '0 0 4px 4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px'
                        }}>
                          <TrophyIcon style={{ height: isMobile ? '8px' : '10px', width: isMobile ? '8px' : '10px' }} />
                          BEST
                        </div>
                      )}
                      
                      {/* Content with mobile responsive layout */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: isMobile ? '8px' : '12px', 
                        marginBottom: isMobile ? '8px' : '12px'
                      }}>
                        <div style={{
                          minWidth: isMobile ? '24px' : '32px',
                          height: isMobile ? '24px' : '32px',
                          backgroundColor: isOptimal ? '#f59e0b' : isSecondBest ? '#6b7280' : '#3b82f6',
                          color: 'white',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: isMobile ? '10px' : '14px',
                          fontWeight: '600'
                        }}>
                          {individualEntry.id}
                        </div>
                        
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: isMobile ? '4px' : '8px', 
                            marginBottom: isMobile ? '6px' : '8px',
                            flexWrap: 'wrap'
                          }}>
                            <div style={{
                              padding: isMobile ? '1px 4px' : '2px 6px',
                              backgroundColor: '#dcfce7',
                              color: '#166534',
                              fontSize: isMobile ? '10px' : '11px',
                              fontWeight: '600',
                              borderRadius: '4px'
                            }}>
                              {Math.round(individualEntry.completeness_score || 0)}% Complete
                            </div>
                            <div style={{
                              padding: isMobile ? '1px 4px' : '2px 6px',
                              backgroundColor: '#dbeafe',
                              color: '#1d4ed8',
                              fontSize: isMobile ? '10px' : '11px',
                              fontWeight: '600',
                              borderRadius: '4px'
                            }}>
                              {Math.round((individualEntry.quality_score || 0) * 10)}% Quality
                            </div>
                          </div>
                          
                          {/* Content sections with mobile responsive text sizes */}
                          {individualEntry.exosite_residues_standard && (
                            <div style={{ marginBottom: isMobile ? '6px' : '8px' }}>
                              <p style={{ 
                                fontSize: isMobile ? '11px' : '12px', 
                                fontWeight: '600', 
                                color: '#374151', 
                                margin: '0 0 4px 0' 
                              }}>
                                Exosite Residues:
                              </p>
                              <p style={{ 
                                color: '#1e293b', 
                                fontFamily: 'monospace', 
                                fontSize: isMobile ? '11px' : '13px', 
                                backgroundColor: '#f1f5f9', 
                                padding: isMobile ? '4px 6px' : '6px 8px', 
                                borderRadius: '4px',
                                margin: 0,
                                lineHeight: '1.4',
                                wordBreak: 'break-word'
                              }}>
                                {individualEntry.exosite_residues_standard.length > (isMobile ? 60 : 100) 
                                  ? `${individualEntry.exosite_residues_standard.substring(0, isMobile ? 60 : 100)}...`
                                  : individualEntry.exosite_residues_standard
                                }
                              </p>
                            </div>
                          )}
                          
                          {/* Other fields with similar mobile optimizations... */}
                          {individualEntry.binding_partner && (
                            <div style={{ marginBottom: isMobile ? '6px' : '8px' }}>
                              <p style={{ 
                                fontSize: isMobile ? '11px' : '12px', 
                                fontWeight: '600', 
                                color: '#374151', 
                                margin: '0 0 4px 0' 
                              }}>
                                Binding Partner:
                              </p>
                              <p style={{ 
                                color: '#1e293b', 
                                fontSize: isMobile ? '11px' : '13px', 
                                margin: 0 
                              }}>
                                {individualEntry.binding_partner}
                              </p>
                            </div>
                          )}
                          
                          <div style={{ 
                            display: 'flex', 
                            gap: isMobile ? '4px' : '8px', 
                            marginTop: isMobile ? '8px' : '12px',
                            flexWrap: 'wrap'
                          }}>
{(() => {
                              const paperLink = generateManuscriptLink(individualEntry);
                              return paperLink && (
                                <a
                                  href={paperLink.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '4px',
                                    padding: isMobile ? '2px 6px' : '4px 8px',
                                    fontSize: isMobile ? '10px' : '11px',
                                    color: '#3b82f6',
                                    textDecoration: 'none',
                                    border: '1px solid #3b82f6',
                                    borderRadius: '4px',
                                    backgroundColor: 'transparent',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#3b82f6';
                                    e.currentTarget.style.color = 'white';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#3b82f6';
                                  }}
                                >
                                  <LinkIcon style={{ height: isMobile ? '10px' : '12px', width: isMobile ? '10px' : '12px' }} />
                                  {isMobile ? 'Paper' : paperLink.label}
                                </a>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                      
                      {isSelected && (
                        <div style={{
                          position: 'absolute',
                          top: isMobile ? '4px' : '8px',
                          left: isMobile ? '4px' : '8px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          fontSize: isMobile ? '8px' : '10px',
                          fontWeight: '600',
                          padding: isMobile ? '1px 4px' : '2px 6px',
                          borderRadius: '4px'
                        }}>
                          SELECTED
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <div style={{ 
                padding: isMobile ? '8px' : '12px', 
                backgroundColor: '#f0f9ff', 
                borderRadius: '8px',
                border: '1px solid #bae6fd'
              }}>
                <p style={{ 
                  fontSize: isMobile ? '12px' : '13px', 
                  fontWeight: '500', 
                  color: '#0369a1', 
                  margin: '0 0 4px 0' 
                }}>
                  📚 Individual Entry Information
                </p>
                <p style={{ 
                  fontSize: isMobile ? '11px' : '12px', 
                  color: '#0369a1', 
                  margin: 0,
                  lineHeight: '1.4'
                }}>
                  Each entry represents a distinct experimental finding from the literature. The <strong>Best Entry</strong> has the highest quality scores. {!isMobile && 'Click any entry to select it as your reference. All entries are consolidated from multiple source papers.'}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'evidence' && evidence.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ 
                color: '#64748b', 
                fontSize: isMobile ? '12px' : '14px', 
                margin: 0 
              }}>
                Evidence information would be displayed here when available.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExositeCard;